// @ts-nocheck
import React from "react";
import { useTranslation } from "react-i18next";

const FieldRenderer = ({ fields, selectedFields, onFieldChange }) => {
  const { t } = useTranslation();

  const handleChange = (fieldId, value) => {
    const updatedFields = [...fields];
    const changedField = updatedFields.find((o) => o.id === fieldId);

    if (changedField && changedField.onChange) {
      const result = changedField.onChange(value, updatedFields);
      if (result) {
        Object.keys(result).forEach((key) => {
          const targetField = updatedFields.find((o) => o.id === key);
          if (targetField) {
            Object.assign(targetField, result[key]);
          }
        });
      }
    }

    onFieldChange(fieldId, value, updatedFields);
  };

  const renderField = (field) => {
    const baseInputClass =
      "w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm";

    switch (field.type) {
      case "dropdown":
        return (
          <div>
            <select
              className={baseInputClass}
              onChange={(e) => handleChange(field.id, e.target.value)}
              value={selectedFields[field.id] || ""}
            >
              <option value="">{t("promptGenerator.options.select")}</option>
              {field.choices.map((choice) => (
                <option
                  key={choice}
                  value={typeof choice === "object" ? choice.value : choice}
                >
                  {typeof choice === "object" ? choice.value : choice}
                </option>
              ))}
              {field.allowOther && (
                <option value="OTHER_VALUE">
                  {t("promptGenerator.options.other")}
                </option>
              )}
            </select>
            {field.allowOther && selectedFields[field.id] === "OTHER_VALUE" && (
              <input
                type="text"
                className={`${baseInputClass} mt-2`}
                placeholder={t("promptGenerator.options.otherPlaceholder")}
                value={selectedFields[`${field.id}_other`] || ""}
                onChange={(e) =>
                  handleChange(`${field.id}_other`, e.target.value)
                }
              />
            )}
          </div>
        );
      case "radio":
        return (
          <div className="space-y-2">
            {field.choices.map((choice) => (
              <label key={choice} className="flex items-center space-x-2">
                <input
                  type="radio"
                  className="form-radio text-blue-500 focus:ring-blue-500"
                  name={field.id}
                  value={choice}
                  checked={selectedFields[field.id] === choice}
                  onChange={(e) => handleChange(field.id, e.target.value)}
                />
                <span className="text-gray-700">{choice}</span>
              </label>
            ))}
            {field.allowOther && (
              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  className="form-radio text-blue-500 focus:ring-blue-500"
                  name={field.id}
                  value="OTHER_VALUE"
                  checked={selectedFields[field.id] === "OTHER_VALUE"}
                  onChange={(e) => handleChange(field.id, e.target.value)}
                />
                <span className="text-gray-700">
                  {t("promptGenerator.options.other")}
                </span>
                {selectedFields[field.id] === "OTHER_VALUE" && (
                  <input
                    type="text"
                    className={`${baseInputClass} ml-2`}
                    placeholder={t("promptGenerator.options.otherPlaceholder")}
                    value={selectedFields[`${field.id}_other`] || ""}
                    onChange={(e) =>
                      handleChange(`${field.id}_other`, e.target.value)
                    }
                  />
                )}
              </label>
            )}
          </div>
        );
      case "checkbox":
        return (
          <div className="space-y-2">
            {field.choices.map((choice) => (
              <label key={choice.value} className="flex items-start space-x-2">
                <input
                  type="checkbox"
                  className="form-checkbox text-blue-500 focus:ring-blue-500 mt-1"
                  checked={
                    selectedFields[field.id]?.includes(choice.value) || false
                  }
                  onChange={(e) => {
                    const updatedChoices = [
                      ...(selectedFields[field.id] || []),
                    ];
                    if (e.target.checked) {
                      updatedChoices.push(choice.value);
                    } else {
                      const index = updatedChoices.indexOf(choice.value);
                      if (index > -1) updatedChoices.splice(index, 1);
                    }
                    handleChange(field.id, updatedChoices);
                  }}
                />
                <div>
                  <span className="text-gray-700">{choice.title}</span>
                  <p className="text-sm text-gray-500">{choice.description}</p>
                </div>
              </label>
            ))}
            {field.allowOther && (
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  className="form-checkbox text-blue-500 focus:ring-blue-500"
                  value="OTHER_VALUE"
                  checked={
                    selectedFields[field.id]?.includes("OTHER_VALUE") || false
                  }
                  onChange={(e) => {
                    const updatedChoices = selectedFields[field.id] || [];
                    if (e.target.checked) {
                      updatedChoices.push("OTHER_VALUE");
                    } else {
                      const index = updatedChoices.indexOf("OTHER_VALUE");
                      if (index > -1) updatedChoices.splice(index, 1);
                    }
                    handleChange(field.id, updatedChoices);
                  }}
                />
                <span className="text-gray-700">
                  {t("promptGenerator.options.other")}
                </span>
                {selectedFields[field.id]?.includes("OTHER_VALUE") && (
                  <input
                    type="text"
                    className={`${baseInputClass} ml-2`}
                    placeholder={t("promptGenerator.options.otherPlaceholder")}
                    value={selectedFields[`${field.id}_other`] || ""}
                    onChange={(e) =>
                      handleChange(`${field.id}_other`, e.target.value)
                    }
                  />
                )}
              </label>
            )}
          </div>
        );
      case "input":
        return (
          <input
            type={field.inputType || "text"}
            className={baseInputClass}
            value={selectedFields[field.id] || ""}
            onChange={(e) => handleChange(field.id, e.target.value)}
            placeholder={field.placeholder}
          />
        );
      case "textarea":
        return (
          <textarea
            className={`${baseInputClass} h-24`}
            value={selectedFields[field.id] || ""}
            onChange={(e) => handleChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            rows={4}
          />
        );
      default:
        return null;
    }
  };

  const formatKey = (key) => {
    // Split the key into words based on camel case or snake case
    const words = key.replace(/([A-Z])/g, " $1").split(/_|\s+/);

    // Capitalize the first letter of each word
    const capitalizedWords = words.map(
      (word) => word.charAt(0).toUpperCase() + word.slice(1)
    );

    // Join the words with spaces
    return capitalizedWords.join(" ");
  };

  return (
    <div>
      <h3 className="text-xl font-semibold text-gray-900 mb-4">
        {t("promptGenerator.options.common.select")}
      </h3>
      <div className="space-y-6">
        {fields.map((field) => {
          if (field.conditional && !field.conditional(selectedFields)) {
            return null;
          }
          return (
            <div key={field.id} className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                {t(
                  `promptGenerator.options.fields.${field.id}`,
                  formatKey(field.id)
                )}
                {!field.optional && (
                  <span className="ml-1 text-xs text-blue-600">
                    ({t("promptGenerator.options.common.recommended")})
                  </span>
                )}
              </label>
              {renderField(field)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FieldRenderer;
